import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CircleDollarSign, Info, Pencil, Plus, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  useAdminRates,
  useCreateRate,
  useDeactivateRate,
  useUpdateRate,
  useAdminNiches,
  useAdminCategories,
} from '@/hooks/useAdmin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import type { InviteCategory, OutreachRate } from '@/api/types';
import { creatorTypeLabel, rateTypeLabel } from '@/lib/labels';
import { formatMoney } from '@/lib/money';

const REPRICE_NOTE =
  "Rate changes also re-price invites that haven't been paid yet, at the next morning check.";

const RATE_TYPES: FormValues['rateType'][] = ['LOG_BONUS', 'JOIN_BONUS', 'POST_BASE'];

interface FormValues {
  category: InviteCategory;
  rateType: 'LOG_BONUS' | 'JOIN_BONUS' | 'POST_BASE';
  niche: string;
  amount: string;
}

const EMPTY: FormValues = {
  category: '',
  rateType: 'LOG_BONUS',
  niche: '',
  amount: '0',
};

export function AdminRates(): JSX.Element {
  const rates = useAdminRates();
  const niches = useAdminNiches();
  const categories = useAdminCategories();
  const create = useCreateRate();
  const deactivate = useDeactivateRate();
  const update = useUpdateRate();
  const [editing, setEditing] = useState<OutreachRate | null>(null);
  const typeName = useMemo(() => {
    const map = new Map((categories.data ?? []).map((c) => [c.code, c.displayName]));
    return (code: string) => map.get(code) ?? creatorTypeLabel(code);
  }, [categories.data]);
  const nicheName = useMemo(() => {
    const map = new Map(
      (niches.data ?? []).map((n) => [n.code, n.isHidden ? `${n.displayName} (hidden)` : n.displayName]),
    );
    return (code: string) => map.get(code) ?? code;
  }, [niches.data]);
  const describe = (r: OutreachRate): string =>
    `${typeName(r.category)} · ${rateTypeLabel(r.rateType)}${r.niche ? ` · ${nicheName(r.niche)} bonus` : ''}`;
  const editForm = useForm<{ amount: string }>({ defaultValues: { amount: '0' } });

  function openEdit(rate: OutreachRate): void {
    editForm.reset({ amount: rate.amount });
    setEditing(rate);
  }

  async function onEdit(values: { amount: string }): Promise<void> {
    if (!editing) return;
    try {
      await update.mutateAsync({ id: editing.id, body: { amount: values.amount } });
      toast.success('Rate updated. Unpaid invites are re-priced at the next morning check.');
      setEditing(null);
    } catch (err) {
      toast.error(`Couldn't update the rate: ${(err as Error).message}`);
    }
  }
  const [showForm, setShowForm] = useState(false);
  const form = useForm<FormValues>({ defaultValues: EMPTY });

  async function onSubmit(values: FormValues): Promise<void> {
    try {
      await create.mutateAsync({
        category: values.category,
        rateType: values.rateType,
        niche: values.niche || null,
        amount: values.amount,
      });
      toast.success('Rate added');
      form.reset(EMPTY);
      setShowForm(false);
    } catch (err) {
      toast.error(`Couldn't add the rate: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rates"
        description="What an invite earns for each creator type: when it's logged, when the creator joins, and per approved post. A rate with a niche is a bonus added on top of the base rate when the invite's first niche matches."
        actions={
          <Button
            onClick={() => {
              form.reset(EMPTY);
              setShowForm(true);
            }}
          >
            <Plus className="h-4 w-4" /> Add rate
          </Button>
        }
      />

      <p className="flex max-w-[75ch] items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        {REPRICE_NOTE}
      </p>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>New rate</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-3" noValidate>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1">
                  <Label>Creator type</Label>
                  <Combobox
                    value={form.watch('category')}
                    onChange={(v) =>
                      form.setValue('category', v as FormValues['category'], {
                        shouldValidate: true,
                      })
                    }
                    options={(categories.data ?? [])
                      .filter((c) => c.isActive)
                      .map((c) => ({
                        value: c.code,
                        label: c.displayName,
                      }))}
                    placeholder="Pick a creator type"
                  />
                </div>
                <div className="grid gap-1">
                  <Label>When it pays</Label>
                  <Combobox
                    value={form.watch('rateType')}
                    onChange={(v) =>
                      form.setValue('rateType', v as FormValues['rateType'], {
                        shouldValidate: true,
                      })
                    }
                    options={RATE_TYPES.map((t) => ({ value: t, label: rateTypeLabel(t) }))}
                    placeholder="Pick when it pays"
                  />
                </div>
              </div>
              <div className="grid gap-1">
                <Label>Niche (optional; leave empty for the base rate)</Label>
                <Combobox
                  value={form.watch('niche')}
                  onChange={(v) => form.setValue('niche', v, { shouldDirty: true })}
                  options={(niches.data ?? [])
                    .filter((n) => {
                      // Hidden niches can't gain new bonuses (the API refuses too).
                      if (n.isHidden) return false;
                      const cat = form.watch('category');
                      if (!cat) return true;
                      return n.categoryCodes.length === 0 || n.categoryCodes.includes(cat);
                    })
                    .map((n) => ({
                      value: n.code,
                      label: n.displayName,
                      ...(n.categoryCodes.length === 0 ? { hint: 'All creator types' } : {}),
                    }))}
                  allowAll
                  allLabel="No niche (base rate)"
                  placeholder="Search niches…"
                />
              </div>
              <div className="grid gap-1">
                <Label htmlFor="amount">Amount ($)</Label>
                <Input
                  id="amount"
                  inputMode="decimal"
                  {...form.register('amount', { required: true })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  Create
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>
              Edit rate · {describe(editing)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={editForm.handleSubmit(onEdit)} className="grid gap-3" noValidate>
              <div className="grid gap-1">
                <Label htmlFor="editAmount">Amount ($)</Label>
                <Input
                  id="editAmount"
                  inputMode="decimal"
                  {...editForm.register('amount', { required: true })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={update.isPending}>
                  Save
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <DataTable>
        <DataTableHeader>
          <tr>
            <DataTableHeaderCell>Creator type</DataTableHeaderCell>
            <DataTableHeaderCell>When it pays</DataTableHeaderCell>
            <DataTableHeaderCell>Niche</DataTableHeaderCell>
            <DataTableHeaderCell numeric>Amount</DataTableHeaderCell>
            <DataTableHeaderCell />
          </tr>
        </DataTableHeader>
        <tbody>
          {(rates.data ?? []).map((r) => (
            <DataTableRow key={r.id}>
              <td className="px-3 py-2">
                <span className="inline-flex items-center gap-2 font-medium">
                  <CircleDollarSign
                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                    strokeWidth={2}
                    aria-hidden
                  />
                  {typeName(r.category)}
                </span>
              </td>
              <td className="px-3 py-2" data-label="When it pays">{rateTypeLabel(r.rateType)}</td>
              <td className="px-3 py-2 text-muted-foreground" data-label="Niche">
                {r.niche ? `${nicheName(r.niche)} bonus` : 'Base rate'}
              </td>
              <td className="px-3 py-2 text-right" data-label="Amount">${formatMoney(r.amount)}</td>
              <td className="actions px-3 py-2 text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Edit ${describe(r)} rate`}
                    title="Edit the rate amount."
                    onClick={() => openEdit(r)}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <ConfirmDialog
                    title={`Turn off ${describe(r)}?`}
                    description={
                      <>
                        Stops the ${formatMoney(r.amount)} rate from applying. Amounts already paid
                        stay paid; invites that haven&apos;t been paid yet are re-priced without it at
                        the next morning check. Add the rate again to bring it back.
                      </>
                    }
                    tone="destructive"
                    confirmLabel="Turn off rate"
                    pending={deactivate.isPending}
                    onConfirm={() =>
                      deactivate.mutateAsync(r.id).then(
                        () => {
                          toast.success('Rate turned off');
                        },
                        (err: Error) => {
                          toast.error(`Couldn't turn off the rate: ${err.message}`);
                        },
                      )
                    }
                    trigger={
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        aria-label={`Turn off ${describe(r)} rate`}
                        title="Stop applying this rate."
                      >
                        <XCircle className="h-3.5 w-3.5" /> Turn off
                      </Button>
                    }
                  />
                </div>
              </td>
            </DataTableRow>
          ))}
        </tbody>
      </DataTable>
    </div>
  );
}
